import React, {Component} from 'react';
import {connect} from 'react-redux';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import {Card, Input, Grid, Button, Icon, Dropdown} from 'semantic-ui-react';
import {noTimeZoneShortISODate, dateFormatPicker} from '../../../common/constants/datesFormat';
import {partClassification} from '../../../common/dictionaries/classification';
import {hasAccessPermission} from '../../../common/permissions/validations';
import {resources, scopes} from '../../../common/permissions/dictionary';
import {paths} from '../../../common/dictionaries/urls';
import {Actions} from '../../../common/actions';
import SalesTable from './table';

const INITIAL_STATE = {
  'startDateFilter': moment().format(noTimeZoneShortISODate),
  'endDateFilter': moment().format(noTimeZoneShortISODate),
  'classificationFilter': [],
  'warehouseFilter': [],
};

class PartsSalesList extends Component {
  constructor(props) {
    super(props);
    this.state = INITIAL_STATE;
    this.onResetFiltersClick = this.onResetFiltersClick.bind(this);
    this.onSearchClick = this.onSearchClick.bind(this);
    this.onDownloadClick = this.onDownloadClick.bind(this);
    this.handleDropdownChange = this.handleDropdownChange.bind(this);
    this.changeEndDate = this.changeEndDate.bind(this);
    this.changeStartDate = this.changeStartDate.bind(this);
  }

  componentDidMount() {
    if (
      !hasAccessPermission(
        this.props.permissions,
        resources.PartsSalesReport,
        scopes.See
      )
    ) {
      this.props.navigateTo(paths.AccessDenied);
    }
    this.props.fetchWarehouses();
    this.onSearchClick();
  }

  buildParameters() {
    const parameters = {};
    const {
      startDateFilter,
      endDateFilter,
      classificationFilter,
      warehouseFilter,
    } = this.state;

    if (startDateFilter) {
      parameters.startDate = startDateFilter;
      parameters.endDate = endDateFilter;
    }
    if (classificationFilter.length) {
      parameters.classification = classificationFilter;
    }
    if (warehouseFilter.length) {
      parameters.warehouse = warehouseFilter;
    }
    return parameters;
  }

  onResetFiltersClick() {
    this.setState(INITIAL_STATE, () => this.onSearchClick());
  }

  onDownloadClick() {
    const parameters = this.buildParameters();
    this.props.downloadPartsSalesList(parameters);
  }

  onSearchClick() {
    const parameters = this.buildParameters();
    this.props.fetchPartsSalesList(parameters);
  }

  updateSearchFilters(value, filterName) {
    const newState = {};
    newState[filterName] = value;
    this.setState(newState);
  }

  changeStartDate(date) {
    const dateFormatted = moment(date).format(noTimeZoneShortISODate);
    const {startDateFilter, endDateFilter} = this.state;

    if (dateFormatted === startDateFilter) {
      return;
    }
    this.updateSearchFilters(dateFormatted, 'startDateFilter');
    if (!endDateFilter || (endDateFilter && date > moment(endDateFilter))) {
      this.updateSearchFilters(dateFormatted, 'endDateFilter');
    }
  }

  changeEndDate(date) {
    const dateFormatted = moment(date).format(noTimeZoneShortISODate);

    if (dateFormatted === this.state.endDateFilter) {
      return;
    }
    this.updateSearchFilters(dateFormatted, 'endDateFilter');
  }

  handleDropdownChange(event, target) {
    const {name, value} = target;
    this.updateSearchFilters(value, name);
  }

  getClassificationOptions() {
    return Object.keys(partClassification).map((element) => {
      return {'text': partClassification[element], 'value': element};
    });
  }

  parseWarehouses(warehouses) {
    return warehouses.map((item) => ({'text': item.code, 'value': item.code}));
  }
  
  renderFiltersSection() {
    const {
      startDateFilter,
      endDateFilter,
      classificationFilter,
      warehouseFilter,
    } = this.state;
    const {loadingWarehouses, warehouses, loading, downloading} = this.props;

    return (
      <div className='filters-section'>
        <Grid textAlign='left'>
          <Grid.Row>
            <Grid.Column width={2}>
              <h4>Fecha Inicio</h4>
            </Grid.Column>
            <Grid.Column width={2}>
              <DatePicker
                customInput={<Input icon='calendar' size='small' />}
                dateFormat={dateFormatPicker}
                selected={
                  startDateFilter ?
                    moment(startDateFilter, noTimeZoneShortISODate).toDate() :
                    null
                }
                onChange={this.changeStartDate}
                disabled={loading}
                maxDate={moment().toDate()}
                locale='es'
                placeholderText='Fecha Inicio'
              />
            </Grid.Column>
            <Grid.Column width={2}>
              <h4>Fecha Fin</h4>
            </Grid.Column>
            <Grid.Column width={2}>
              <DatePicker
                customInput={<Input icon='calendar' size='small' />}
                dateFormat={dateFormatPicker}
                selected={
                  endDateFilter ?
                    moment(endDateFilter, noTimeZoneShortISODate).toDate() :
                    null
                }
                onChange={this.changeEndDate}
                disabled={!startDateFilter || loading}
                minDate={moment(startDateFilter).toDate()}
                maxDate={moment().toDate()}
                placeholderText='Fecha Fin'
                locale='es'
              />
            </Grid.Column>
            <Grid.Column width={1}>
              <h4>Tipo</h4>
            </Grid.Column>
            <Grid.Column width={3}>
              <Dropdown
                fluid
                selection
                name='classificationFilter'
                options={this.getClassificationOptions()}
                value={classificationFilter}
                multiple
                disabled={loading || downloading}
                onChange={this.handleDropdownChange}
                placeholder='Tipo'
              />
            </Grid.Column>
            <Grid.Column width={1}>
              <h4>Bodega</h4>
            </Grid.Column>
            <Grid.Column width={3}>
              <Dropdown
                fluid
                selection
                name='warehouseFilter'
                options={this.parseWarehouses(warehouses)}
                value={warehouseFilter}
                multiple
                disabled={loadingWarehouses || loading || downloading}
                onChange={this.handleDropdownChange}
                placeholder='Bodega'
              />
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </div>
    );
  }

  render() {
    const {loadingWarehouses, loading, downloading} = this.props;
    const searchEnabled = loadingWarehouses || loading || downloading;

    return (
      <div className='parts-sales-list'>
        <Card raised fluid>
          <div className='title-bar'>
            <div className='title'>
              <h2>Reporte de Ventas de Repuestos</h2>
              <div className='title-bar-actions'>
                <Button
                  onClick={this.onResetFiltersClick}
                  disabled={searchEnabled}
                  secondary
                >
                  Limpiar
                </Button>
                <Button
                  onClick={this.onDownloadClick}
                  className='download'
                  primary
                  disabled={searchEnabled}
                  loading={downloading}
                >
                  <Icon name='download' />
                  <span>Excel</span>
                </Button>
                <Button
                  onClick={this.onSearchClick}
                  disabled={searchEnabled}
                  loading={loading}
                  primary
                >
                  Buscar
                </Button>
              </div>
            </div>

            {this.renderFiltersSection()}
          </div>
        </Card>

        <SalesTable />
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    'loadingWarehouses': state.warehouses.loading,
    'warehouses': state.warehouses.list || [],
    'loading': state.partsSales.loading,
    'downloading': state.partsSales.downloading,
    'menuSelected': state.menu.selected,
    'permissions': state.session.permissions,
  };
};

const {
  fetchWarehouses,
  fetchPartsSalesList,
  downloadPartsSalesList,
  navigateTo,
} = Actions;

export default connect(mapStateToProps, {
  fetchWarehouses,
  fetchPartsSalesList,
  downloadPartsSalesList,
  navigateTo,
})(PartsSalesList);
